import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '../constant';
import { getAccessToken, getSessionGeneration, getSessionIdentity, isSessionReplaced, SESSION_MARKER_KEYS } from '../session';
import { requestLanguage } from '../requestLanguage';
import { isTransientSessionError, SessionChangedError } from './errors';
import { endSession, goToLogin, leaveEndedSupport } from './navigation';
import { refreshAccessToken } from './refresh';
import { canReplayAfter401 } from './replay';
import type { HeldSession } from './sessionLock';
import { LOGIN_ENDPOINT } from './webSession';

export type RetriableConfig = AxiosRequestConfig & {
  _retried?: boolean;
  /**
   * 要求を最初に送り出した時点のセッション（識別子と更新の世代）。401 の後の更新・
   * 再送は、「今の」セッションではなくこの値を基準に判断する。同じタブで
   * ログインし直した後に、前の利用者の要求を新しいトークンで送らないため。
   */
  _sentWith?: HeldSession;
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

/**
 * 別のタブでログアウト・ログインし直されたら、このタブをログイン画面へ戻す。
 * 新しいセッションの印は消さない（別のタブで使われている）。
 * 同じセッションの更新（世代の変化）では何もしない。
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && !SESSION_MARKER_KEYS.includes(event.key)) return;
    if (isSessionReplaced({ countLogout: true })) goToLogin();
  });
}

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.url === LOGIN_ENDPOINT) return withLanguage(config);
    // 別のタブでセッションが入れ替わっていたら、このタブで準備した要求を
    // 新しいセッションで送らない。
    if (isSessionReplaced()) {
      goToLogin();
      return Promise.reject(new SessionChangedError());
    }
    const retriable = config as RetriableConfig;
    if (retriable._sentWith === undefined) {
      // 最初の送出。このときのセッションを要求に記録する。
      retriable._sentWith = { session: getSessionIdentity(), generation: getSessionGeneration() };
    } else if (retriable._sentWith.session !== getSessionIdentity()) {
      // 再送。最初に送ったときのセッションではなくなっていたら送らない。
      return Promise.reject(new SessionChangedError());
    }
    // ADM-17。再読み込み直後・新しいタブでは access がメモリに無い。セッションの印が
    // あれば、送る前に Cookie の refresh で取り直す。
    if (!getAccessToken() && retriable._sentWith.session !== null) {
      try {
        await refreshAccessToken(retriable._sentWith.session);
      } catch (refreshError) {
        // YAC-2026-009 PR7b。サポートが終わった（401 support_access_ended）なら、ログインではなく終わりの画面へ。
        if (leaveEndedSupport(refreshError)) return Promise.reject(refreshError);
        if (!isTransientSessionError(refreshError)) endSession(retriable._sentWith);
        return Promise.reject(refreshError);
      }
    }
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      // tokenが無いのに `Bearer null` を送ると、サーバー側のログが読めなくなる。
      delete config.headers.Authorization;
    }
    return withLanguage(config);
  },
  (error) => Promise.reject(error)
);

/** サーバーは要求の言語でエラー文を返す。表示言語（i18n と同じ保存値）に合わせる。 */
function withLanguage<T extends AxiosRequestConfig>(config: T): T {
  if (config.headers) {
    (config.headers as any)['Accept-Language'] = requestLanguage();
  }
  return config;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = (error.config ?? {}) as RetriableConfig;
    const status = error.response?.status;

    // 応答が無い＝通信断・timeout・CORS拒否。認証の問題と混同しない。
    if (status === undefined) {
      return Promise.reject(error);
    }

    // ログイン自体の失敗は、そのまま呼び出し側へ返す。
    if (originalRequest.url === LOGIN_ENDPOINT) {
      return Promise.reject(error);
    }

    // YAC-2026-009 PR7b。サポートのセッションが終わった。更新しても同じ 401 なので、更新も再送もせず終わりの画面へ。
    if (leaveEndedSupport(error)) {
      return Promise.reject(error);
    }

    if (status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }
    if (!canReplayAfter401(originalRequest, error.response?.data)) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;
    // 判断の基準は、要求を送り出した時点のセッション（今のものではない）。
    const held: HeldSession = originalRequest._sentWith ?? {
      session: getSessionIdentity(),
      generation: getSessionGeneration(),
    };
    if (held.session === null) {
      // セッションが無い（ログイン前）。更新もやり直しもしない。
      return Promise.reject(error);
    }
    if (getSessionIdentity() !== held.session) {
      // 応答を待つ間にセッションが替わった（このタブ・別のタブでのログインし直し、
      // ログアウト）。前のセッションの要求を、今のセッションで更新・再送しない。
      if (isSessionReplaced({ countLogout: true })) goToLogin();
      return Promise.reject(new SessionChangedError());
    }
    try {
      await refreshAccessToken(held.session);
    } catch (refreshError) {
      if (isTransientSessionError(refreshError) || leaveEndedSupport(refreshError)) {
        return Promise.reject(refreshError);
      }
      endSession(held);
      return Promise.reject(refreshError);
    }
    if (getSessionIdentity() !== held.session) {
      // 更新を待つ間にログインし直された。このタブで準備した要求を別のセッション
      // （別の利用者かもしれない）のトークンで送らない。
      endSession(held);
      return Promise.reject(new SessionChangedError());
    }
    return axiosInstance(originalRequest);
  }
);

export default axiosInstance;
